import { useState } from 'react';
import { useAuthStore } from '../../stores/useAuthStore';
import { accountApi } from '../../services/api';
import { AppleEmoji } from '../../components/AppleEmoji';
import { translateVerificationError } from '../../utils/verificationErrors';

export default function VerificationView() {
  const user = useAuthStore((s) => s.user)!;
  const setUser = useAuthStore((s) => s.setUser);

  return (
    <div className="page-view">
      <div className="page-view__header">
        <h1 className="page-view__title">
          <AppleEmoji emoji="🛡" size={32} /> Верификация
        </h1>
        <p className="page-view__subtitle">
          Подтвердите email, телефон и укажите ФИО, чтобы совершать покупки.
        </p>
      </div>

      <EmailBlock verified={user.email_verified} email={user.email} onDone={setUser} />
      <PhoneBlock verified={user.phone_verified} phone={user.phone_number} onDone={setUser} />
      <FullNameBlock fullName={user.full_name} onDone={setUser} />
    </div>
  );
}

function FullNameBlock({
  fullName,
  onDone,
}: {
  fullName: string | null;
  onDone: (u: any) => void;
}) {
  const [value, setValue] = useState(fullName || '');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      const { data } = await accountApi.setFullName(value.trim());
      onDone(data);
      setSaved(true);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  };

  const changed = value.trim() !== (fullName || '').trim();
  const valid = value.trim().length >= 2;

  return (
    <div className="verify-card">
      <div className="verify-card__head">
        <AppleEmoji emoji="🪪" size={28} />
        <div>
          <div className="verify-card__title">ФИО <span style={{ color: '#ff8080' }}>*</span></div>
          <div className="verify-card__hint">
            Обязательно. Укажите фамилию, имя и отчество как в паспорте.
          </div>
        </div>
        {fullName && !changed && (
          <div className="verify-card__badge">
            <AppleEmoji emoji="✓" size={14} /> Сохранено
          </div>
        )}
      </div>

      <div className="verify-card__body">
        <input
          type="text"
          placeholder="Иванов Иван Иванович"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="verify-input"
          maxLength={255}
        />
        <div
          className="verify-error"
          style={{
            background: 'rgba(255, 186, 76, 0.1)',
            color: '#ffba4c',
          }}
        >
          <AppleEmoji emoji="⚠️" size={14} /> Внимание: за предоставление ложных
          данных ваш аккаунт может быть заблокирован.
        </div>
        <button
          className="verify-btn"
          onClick={save}
          disabled={busy || !valid || !changed}
        >
          {busy ? 'Сохраняем...' : fullName ? 'Обновить' : 'Сохранить'}
        </button>
        {saved && !changed && (
          <div className="verify-sent">
            <AppleEmoji emoji="✓" size={14} /> ФИО сохранено
          </div>
        )}
        {err && <div className="verify-error">{err}</div>}
      </div>
    </div>
  );
}

function EmailBlock({
  verified,
  email,
  onDone,
}: {
  verified: boolean;
  email: string | null;
  onDone: (u: any) => void;
}) {
  const [localEmail, setLocalEmail] = useState(email || '');
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (verified) {
    return (
      <div className="verify-card verify-card--done">
        <div className="verify-card__head">
          <AppleEmoji emoji="📧" size={28} />
          <div>
            <div className="verify-card__title">Email</div>
            <div className="verify-card__value">{email}</div>
          </div>
          <div className="verify-card__badge">
            <AppleEmoji emoji="✓" size={14} /> Подтверждён
          </div>
        </div>
      </div>
    );
  }

  const sendCode = async () => {
    setBusy(true);
    setErr(null);
    try {
      await accountApi.emailSendCode(localEmail);
      setSent(true);
    } catch (e: any) {
      setErr(translateVerificationError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setErr(null);
    try {
      const { data } = await accountApi.emailVerify(code);
      onDone(data);
    } catch (e: any) {
      setErr(translateVerificationError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="verify-card">
      <div className="verify-card__head">
        <AppleEmoji emoji="📧" size={28} />
        <div>
          <div className="verify-card__title">Подтверждение email</div>
          <div className="verify-card__hint">Пришлём 6-значный код на вашу почту.</div>
        </div>
      </div>

      <div className="verify-card__body">
        {!sent ? (
          <>
            <input
              type="email"
              placeholder="your@email.com"
              value={localEmail}
              onChange={(e) => setLocalEmail(e.target.value)}
              className="verify-input"
            />
            <button className="verify-btn" onClick={sendCode} disabled={busy || !localEmail}>
              {busy ? 'Отправляем...' : 'Отправить код'}
            </button>
          </>
        ) : (
          <>
            <div className="verify-sent">
              <AppleEmoji emoji="✉" size={18} /> Код отправлен на <b>{localEmail}</b>
            </div>
            <input
              placeholder="Введите 6-значный код"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="verify-input verify-input--code"
              maxLength={6}
              inputMode="numeric"
            />
            <div className="verify-actions">
              <button className="verify-btn" onClick={verify} disabled={busy || code.length < 4}>
                Подтвердить
              </button>
              <button className="verify-btn verify-btn--link" onClick={sendCode} disabled={busy}>
                Отправить ещё раз
              </button>
            </div>
          </>
        )}
        {err && <div className="verify-error">{err}</div>}
      </div>
    </div>
  );
}

function PhoneBlock({
  verified,
  phone,
  onDone,
}: {
  verified: boolean;
  phone: string | null;
  onDone: (u: any) => void;
}) {
  const [localPhone, setLocalPhone] = useState(phone || '');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (verified) {
    return (
      <div className="verify-card verify-card--done">
        <div className="verify-card__head">
          <AppleEmoji emoji="📱" size={28} />
          <div>
            <div className="verify-card__title">Телефон</div>
            <div className="verify-card__value">{phone}</div>
          </div>
          <div className="verify-card__badge">
            <AppleEmoji emoji="✓" size={14} /> Подтверждён
          </div>
        </div>
      </div>
    );
  }

  const startAndSend = async () => {
    setBusy(true);
    setErr(null);
    try {
      const { data } = await accountApi.phoneStart(localPhone);
      if (!data.success || !data.session_id) {
        setErr(translateVerificationError(data.error) || 'Ошибка');
        return;
      }
      setSessionId(data.session_id);
      await accountApi.phoneSendCode(data.session_id, localPhone);
      setSent(true);
    } catch (e: any) {
      setErr(translateVerificationError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!sessionId) return;
    setBusy(true);
    setErr(null);
    try {
      const { data } = await accountApi.phoneVerify(sessionId, code);
      onDone(data);
    } catch (e: any) {
      setErr(translateVerificationError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="verify-card">
      <div className="verify-card__head">
        <AppleEmoji emoji="📱" size={28} />
        <div>
          <div className="verify-card__title">Подтверждение телефона</div>
          <div className="verify-card__hint">Код придёт через Telegram.</div>
        </div>
      </div>

      <div className="verify-card__body">
        {!sent ? (
          <>
            <input
              type="tel"
              placeholder="+7 999 123-45-67"
              value={localPhone}
              onChange={(e) => setLocalPhone(e.target.value)}
              className="verify-input"
            />
            <button className="verify-btn" onClick={startAndSend} disabled={busy || !localPhone}>
              {busy ? 'Отправляем...' : 'Отправить код'}
            </button>
          </>
        ) : (
          <>
            <div className="verify-sent">
              <AppleEmoji emoji="💬" size={18} /> Код отправлен на <b>{localPhone}</b>
            </div>
            <input
              placeholder="Введите код"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="verify-input verify-input--code"
              maxLength={6}
              inputMode="numeric"
            />
            <div className="verify-actions">
              <button className="verify-btn" onClick={verify} disabled={busy || code.length < 4}>
                Подтвердить
              </button>
              <button
                className="verify-btn verify-btn--link"
                onClick={() => sessionId && accountApi.phoneSendCode(sessionId, localPhone)}
                disabled={busy}
              >
                Отправить ещё раз
              </button>
            </div>
          </>
        )}
        {err && <div className="verify-error">{err}</div>}
      </div>
    </div>
  );
}
